/**
 * Shared plumbing for files stored as RFQ documents: query-thread attachments
 * and (CR-6205) the files on each RFQ line.
 *
 * Downloads must come through the authenticated endpoint: `/media/rfq/**` is
 * hard-blocked as private media, and the endpoint applies the RFQ's access
 * rules (the buyer and invited suppliers only).
 */
import { rfqAPI } from '../../lib/api';
import type { RFQDocument } from '../../lib/api';

/** Rejected before upload so the user hears about it immediately, not after a 413. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/**
 * A file size for people: "820 B", "14 KB", "2.3 MB".
 *
 * @param bytes - size in bytes
 * @returns the size, rounded for display
 */
export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Fetch a private RFQ document as a blob and hand it to the browser as a save.
 *
 * @param doc - the document to download
 */
export async function downloadRfqDocument(doc: RFQDocument): Promise<void> {
  const response = await rfqAPI.downloadDocument(doc.id);
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = doc.file_name || doc.title || 'attachment';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * CR-6205: upload files onto one RFQ line, one after another.
 *
 * Sequential so a failure stops at the file that failed, and the error names
 * it rather than leaving the rest half-attached in an unknown order. The
 * backend only takes line files while the RFQ is a draft.
 *
 * @param rfqId - the RFQ the line belongs to
 * @param lineId - the line the files attach to
 * @param files - files to attach
 * @returns the created documents, in the order given
 * @link LineItemFiles for the RFQ page control that calls this
 */
export async function uploadLineFiles(rfqId: number, lineId: number, files: File[]): Promise<RFQDocument[]> {
  const created: RFQDocument[] = [];
  for (const file of files) {
    const form = new FormData();
    form.append('rfq', String(rfqId));
    form.append('line_item', String(lineId));
    form.append('file', file, file.name);
    // A line file can be a drawing, a spec sheet or a photo; 'other' is
    // never wrong, and the line itself says what the file is for.
    form.append('document_type', 'other');
    form.append('title', file.name);
    // A boolean missing from multipart reads as False server-side.
    form.append('is_active', 'true');
    const response = await rfqAPI.uploadDocument(form);
    created.push(response.data);
  }
  return created;
}
